import { apiClient } from '@/libs/api_client'
import axios from 'axios'
import { BroadcastDevice, ApiResponse } from '@magnetar/admin-api-interfaces'
import { BroadcastDeviceVideoType } from '@magnetar/data-access'
import { BroadcastDeviceVod } from '@/hooks/useBroadcastDeviceVodList'
import { logger } from '@magnetar/utils'

interface CreateBroadcastDeviceData {
  name?: string
  examination_room_ids?: number[]
  broadcast_type: BroadcastDeviceVideoType
  is_active: boolean
}

interface UpdateBroadcastDeviceData {
  examination_room_ids?: number[]
  clinic_id?: number | null
  broadcast_type?: BroadcastDeviceVideoType
  is_active?: boolean
  name?: string
}

interface CreateBroadcastDeviceVodData {
  vod_name: string
  source: string
  broadcast_type: BroadcastDeviceVideoType
  order?: number | null
  is_active: boolean
}

interface UpdateBroadcastDeviceVodData {
  vod_id: number
  vod_name: string
  source: string
  order?: number | null
  is_active: boolean
}

export const broadcastDeviceService = {
  createBroadcastDevice: async (
    clinicId: number,
    data: CreateBroadcastDeviceData,
    token: string,
  ) => {
    try {
      const response = await apiClient.post<ApiResponse<BroadcastDevice>>(
        `/api/clinic/${clinicId}/broadcast-device`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data
      }
      return {
        success: false,
        error: {
          code: 'CREATE_BROADCAST_DEVICE_FAILED',
          message: '建立廣播設備失敗',
        },
      }
    }
  },

  updateBroadcastDevice: async (
    deviceId: number,
    data: UpdateBroadcastDeviceData,
    token: string,
  ) => {
    try {
      const response = await apiClient.put<ApiResponse<BroadcastDevice>>(
        `/api/broadcast-device/${deviceId}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data
      }
      return {
        success: false,
        error: {
          code: 'UPDATE_BROADCAST_DEVICE_FAILED',
          message: '更新廣播設備失敗',
        },
      }
    }
  },

  deleteBroadcastDevice: async (deviceId: number, token: string) => {
    try {
      const response = await apiClient.delete<ApiResponse<void>>(
        `/api/broadcast-device/${deviceId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data
      }
      return {
        success: false,
        error: {
          code: 'DELETE_BROADCAST_DEVICE_FAILED',
          message: '刪除廣播設備失敗',
        },
      }
    }
  },

  // Broadcast Device VOD methods
  createBroadcastDeviceVod: async (
    broadcastDeviceId: number,
    data: CreateBroadcastDeviceVodData,
    token: string,
  ) => {
    try {
      const response = await apiClient.post<ApiResponse<BroadcastDeviceVod>>(
        `/api/broadcast-device/${broadcastDeviceId}/vod`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data
      }
      return {
        success: false,
        error: {
          code: 'CREATE_BROADCAST_DEVICE_VOD_FAILED',
          message: '建立廣播設備影片失敗',
        },
      }
    }
  },

  updateBroadcastDeviceVod: async (data: UpdateBroadcastDeviceVodData, token: string) => {
    try {
      const response = await apiClient.put<ApiResponse<BroadcastDeviceVod>>(
        `/api/broadcast-device-vod/${data.vod_id}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data
      }
      return {
        success: false,
        error: {
          code: 'UPDATE_BROADCAST_DEVICE_VOD_FAILED',
          message: '更新廣播設備影片失敗',
        },
      }
    }
  },

  deleteBroadcastDeviceVod: async (vodId: number, token: string) => {
    try {
      const response = await apiClient.delete<ApiResponse<void>>(
        `/api/broadcast-device-vod/${vodId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data
      }
      return {
        success: false,
        error: {
          code: 'DELETE_BROADCAST_DEVICE_VOD_FAILED',
          message: '刪除廣播設備影片失敗',
        },
      }
    }
  },

  // For backward compatibility
  createExaminationVod: async () => {
    try {
      // This function is kept for backward compatibility
      // It should be updated to use the new broadcast device VOD API
      logger.warn('createExaminationVod is deprecated, use createBroadcastDeviceVod instead')
      return {
        success: false,
        error: {
          code: 'METHOD_DEPRECATED',
          message: 'This method is deprecated, use createBroadcastDeviceVod instead',
        },
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data
      }
      return {
        success: false,
        error: {
          code: 'CREATE_EXAMINATION_VOD_FAILED',
          message: '建立診間影片失敗',
        },
      }
    }
  },
}
